'use client'
import { type FC } from 'react'
import { Center, HStack, Icon, Text } from '@chakra-ui/react'
import { MdStorefront } from 'react-icons/md'
import { TbTruckDelivery } from 'react-icons/tb'
import { GrandRadioCard } from 'ui'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'

export type DeliveryMode = 'DELIVERY' | 'PICKUP'

// Override keys keep the legacy `invoice*` prefix so the existing
// `invoicePaymentTranslations` bundle (provided globally by `CheckoutPageStd`)
// resolves them for both the invoice and upfront flows.
export interface DeliveryMethodOverrides {
  invoiceDeliveryLabel?: string
  invoiceDeliveryDescription?: string
  invoiceDeliveryFreeLabel?: string
  invoicePickupLabel?: string
  invoicePickupDescription?: string
}

interface DeliveryMethodSelectorProps {
  value: DeliveryMode
  onChange: (next: DeliveryMode) => void
  /** When the store has no pickup locations configured, the pickup card is
   *  suppressed and only delivery is offered. */
  pickupAvailable: boolean
  /** Pre-formatted delivery price (e.g. "300 SEK"). Rendered as a bold line
   *  on the Delivery card. Pass `0` to render the "free shipping" label. */
  deliveryCost?: number
  /** Money formatter from the parent — reused so the currency formatting
   *  matches the rest of the checkout. */
  formatCost?: (n: number) => string
}

/**
 * Two-card Delivery / Pick-up switcher used by every payment flow that
 * runs on the DEFAULT shipping branch (no third-party carrier widget).
 * Renders only the delivery card when no pickup locations are configured.
 */
export const DeliveryMethodSelector: FC<DeliveryMethodSelectorProps> = ({
  value,
  onChange,
  pickupAvailable,
  deliveryCost,
  formatCost
}) => {
  const overrides = useOverridesContext<keyof DeliveryMethodOverrides>()

  const deliveryLabel = overrideText('Leverans', overrides?.invoiceDeliveryLabel)
  const deliveryDescription = overrideText(
    'Vi levererar till önskad adress',
    overrides?.invoiceDeliveryDescription
  )
  const deliveryFreeLabel = overrideText('Fri frakt', overrides?.invoiceDeliveryFreeLabel)
  const pickupLabel = overrideText('Hämta i butik', overrides?.invoicePickupLabel)
  const pickupDescription = overrideText(
    'Hämta i vår butik (frakt tas bort)',
    overrides?.invoicePickupDescription
  )

  const resolveDeliveryPriceLabel = () => {
    if (deliveryCost === undefined) return null
    if (deliveryCost === 0) return deliveryFreeLabel
    return formatCost?.(deliveryCost) ?? `${deliveryCost}`
  }

  const deliveryPriceLabel = resolveDeliveryPriceLabel()

  if (!pickupAvailable) {
    return (
      <HStack
        align="center"
        borderColor="border.default"
        borderRadius="md"
        borderWidth="1px"
        gap={3}
        justify="space-between"
        px={4}
        py={3}
      >
        <HStack gap={3}>
          <Icon as={TbTruckDelivery} boxSize={5} color="blue.500" />
          <Text fontSize="sm">{deliveryLabel}</Text>
        </HStack>
        {deliveryPriceLabel ? (
          <Text fontSize="sm" fontWeight="semibold">
            {deliveryPriceLabel}
          </Text>
        ) : null}
      </HStack>
    )
  }

  return (
    <GrandRadioCard.Root
      onChange={(next) => onChange(next as DeliveryMode)}
      size="md"
      value={value}
      variant="outline"
    >
      <HStack alignItems="stretch" gap={4}>
        <GrandRadioCard.Item
          alignItems="center"
          display="flex"
          flex="1"
          justifyContent="center"
          textAlign="center"
          value="DELIVERY"
        >
          <Center flexDirection="column" gap={3} h="100%">
            <Icon as={TbTruckDelivery} boxSize={6} color="blue.500" />
            <GrandRadioCard.ItemContent align="center">
              <GrandRadioCard.ItemText textAlign="center">{deliveryLabel}</GrandRadioCard.ItemText>
              <GrandRadioCard.ItemDescription textAlign="center">
                {deliveryDescription}
              </GrandRadioCard.ItemDescription>
              {deliveryPriceLabel ? (
                <Text fontSize="sm" fontWeight="semibold" mt={1} textAlign="center">
                  {deliveryPriceLabel}
                </Text>
              ) : null}
            </GrandRadioCard.ItemContent>
          </Center>
        </GrandRadioCard.Item>

        <GrandRadioCard.Item
          alignItems="center"
          display="flex"
          flex="1"
          justifyContent="center"
          textAlign="center"
          value="PICKUP"
        >
          <Center flexDirection="column" gap={3} h="100%">
            <Icon as={MdStorefront} boxSize={6} color="blue.500" />
            <GrandRadioCard.ItemContent align="center">
              <GrandRadioCard.ItemText textAlign="center">{pickupLabel}</GrandRadioCard.ItemText>
              <GrandRadioCard.ItemDescription textAlign="center">
                {pickupDescription}
              </GrandRadioCard.ItemDescription>
            </GrandRadioCard.ItemContent>
          </Center>
        </GrandRadioCard.Item>
      </HStack>
    </GrandRadioCard.Root>
  )
}
